import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import {
  ShoppingCart,
  Heart,
  Check,
  Clock,
  Award,
  Sparkles,
} from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { Spinner } from "../components/common/Spinner";
import { usePromotion, getDiscountedPrice } from "../hooks/usePromotion";
import styles from "./PaintKitsPage.module.css";

interface PaintKit {
  id: string;
  title: string;
  description: string;
  price: number;
  image: string;
  size: string;
  collectionId: string | null;
  collectionName: string | null;
  categoryIds: string[];
  inStock: boolean;
  paintingTime?: string;
  framed?: boolean;
  bestSeller?: boolean;
  latestCollection?: boolean;
  difficulty?: string;
}

interface Collection {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

export function PaintKitsPage() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [searchParams] = useSearchParams();
  const [selectedCollection, setSelectedCollection] = useState<string>(
    searchParams.get("collection") || "all",
  );
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>(
    searchParams.get("category") || "all",
  );
  const [paintKits, setPaintKits] = useState<PaintKit[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [realCategories, setRealCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addedToCart, setAddedToCart] = useState<string | null>(null);
  const { promotion, isActive } = usePromotion();
  const kitsDiscountPercent =
    isActive && promotion?.appliesTo.paintingKits
      ? promotion.discountPercent
      : 0;

  useEffect(() => {
    const fetchPaintKits = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/painting-kits/`);
        const data = await response.json();

        // Map backend data to frontend structure
        const mappedKits: PaintKit[] = data.kits.map((kit: any) => {
          // Get cover image: prefer images array, fall back to imageUrl, then placeholder
          let coverImage =
            "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=400&fit=crop";
          if (kit.images && kit.images.length > 0) {
            const defaultImg =
              kit.images.find((img: any) => img.isDefault) || kit.images[0];
            coverImage = defaultImg.url.startsWith("http")
              ? defaultImg.url
              : `${API_BASE_URL}${defaultImg.url}`;
          } else if (kit.imageUrl) {
            coverImage = kit.imageUrl.startsWith("http")
              ? kit.imageUrl
              : `${API_BASE_URL}${kit.imageUrl}`;
          }

          return {
            id: kit._id,
            title: kit.name,
            description: kit.description,
            price: kit.price,
            image: coverImage,
            size: kit.size || "40x50 cm",
            collectionId: kit.productCollection?._id || null,
            collectionName: kit.productCollection?.name || null,
            categoryIds: (kit.categories || []).map((c: any) => c._id),
            inStock: kit.inStock,
            paintingTime: kit.paintingTime,
            framed: kit.framed,
            bestSeller: kit.bestSeller,
            latestCollection: kit.latestCollection,
            difficulty: kit.difficulty,
          };
        });

        setPaintKits(mappedKits);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching paint kits:", error);
        setLoading(false);
      }
    };

    const fetchCollections = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/collections/`);
        const data = await response.json();
        setCollections(
          (data.collections || []).map((c: any) => ({
            id: c._id,
            name: c.name,
          })),
        );
      } catch (error) {
        console.error("Error fetching collections:", error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories/`);
        const data = await response.json();
        setRealCategories(
          (data.categories || []).map((c: any) => ({
            id: c._id,
            name: c.name,
          })),
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchPaintKits();
    fetchCollections();
    fetchCategories();
  }, []);

  const categories = [
    { id: "all", label: "All Kits" },
    ...collections.map((c) => ({ id: c.id, label: c.name })),
  ];

  const categoryFilters = [
    { id: "all", label: "All Categories" },
    ...realCategories.map((c) => ({ id: c.id, label: c.name })),
  ];

  const filteredKits = paintKits
    .filter((kit) =>
      selectedCollection === "all" ? true : kit.collectionId === selectedCollection,
    )
    .filter((kit) =>
      selectedCategoryFilter === "all"
        ? true
        : kit.categoryIds.includes(selectedCategoryFilter),
    );

  return (
    <div className={styles.paintKitsPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Paint by Numbers Kits</h1>
          <p className={styles.heroSubtitle}>
            Discover our collection of beautiful paint by numbers kits for all
            skill levels
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent}>
        <div className={styles.container}>
          {/* Collection Filter */}
          {collections.length > 0 && (
            <div className={styles.filterSection}>
              <h2 className={styles.filterTitle}>Collections</h2>
              <div className={styles.categories}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCollection(category.id)}
                    className={`${styles.categoryBtn} ${selectedCollection === category.id ? styles.categoryActive : ""}`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category Filter */}
          {realCategories.length > 0 && (
            <div className={styles.filterSection}>
              <h2 className={styles.filterTitle}>Categories</h2>
              <div className={styles.categories}>
                {categoryFilters.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategoryFilter(cat.id)}
                    className={`${styles.categoryBtn} ${selectedCategoryFilter === cat.id ? styles.categoryActive : ""}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <Spinner label="Loading paint kits..." size="lg" />
          ) : (
            <div className={styles.productsGrid}>
              {filteredKits.map((kit) => (
                <div
                  key={kit.id}
                  className={styles.productCard}
                  onClick={() => navigate(`/products/kits/${kit.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  {!kit.inStock && (
                    <div className={styles.outOfStock}>Out of Stock</div>
                  )}
                  <div className={styles.imageContainer}>
                    <img
                      src={kit.image}
                      alt={kit.title}
                      className={styles.productImage}
                    />
                    <div className={styles.badgeStack}>
                      {kit.bestSeller && (
                        <div className={styles.bestSellerBadge}>
                          <Award size={14} />
                          Best Seller
                        </div>
                      )}
                      {kit.latestCollection && (
                        <div className={styles.latestBadge}>
                          <Sparkles size={14} />
                          New
                        </div>
                      )}
                      {kit.collectionName && (
                        <span className={styles.collectionBadge}>
                          {kit.collectionName}
                        </span>
                      )}
                    </div>
                    {/* <button
                      className={styles.favoriteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(`Toggle favorite for ${kit.id}`);
                      }}
                    >
                      <Heart size={20} />
                    </button> */}
                  </div>

                  <div className={styles.productInfo}>
                    <div className={styles.productHeader}>
                      {/* <div className={styles.rating}>
                        <Star size={16} className={styles.starIcon} />
                        <span>{kit.rating}</span>
                      </div> */}
                    </div>

                    <h3 className={styles.productTitle}>{kit.title}</h3>
                    {kit.difficulty && (
                      <span
                        className={`${styles.difficultyTag} ${styles[kit.difficulty]}`}
                      >
                        {kit.difficulty.charAt(0).toUpperCase() +
                          kit.difficulty.slice(1)}
                      </span>
                    )}
                    <p className={styles.productDescription}>
                      {kit.description}
                    </p>

                    {kit.paintingTime && (
                      <div className={styles.paintingTime}>
                        <Clock size={14} />
                        <span>{kit.paintingTime}</span>
                      </div>
                    )}

                    <div className={styles.productFooter}>
                      {kitsDiscountPercent > 0 ? (
                        <div className={styles.priceGroup}>
                          <span className={styles.originalPrice}>
                            EGP {kit.price}
                          </span>
                          <span className={`${styles.price} ${styles.discountedPrice}`}>
                            EGP {getDiscountedPrice(kit.price, kitsDiscountPercent)}
                          </span>
                        </div>
                      ) : (
                        <span className={styles.price}>EGP {kit.price}</span>
                      )}
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart({
                            id: kit.id,
                            name: kit.title,
                            price:
                              kitsDiscountPercent > 0
                                ? getDiscountedPrice(kit.price, kitsDiscountPercent)
                                : kit.price,
                            imageUrl: kit.image,
                            type: "kit",
                            size: kit.size,
                          });
                          setAddedToCart(kit.id);
                          setTimeout(() => setAddedToCart(null), 2000);
                        }}
                        disabled={!kit.inStock}
                      >
                        {addedToCart === kit.id ? (
                          <>
                            <Check size={18} />
                            Added!
                          </>
                        ) : (
                          <>
                            <ShoppingCart size={18} />
                            Add to Cart
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredKits.length === 0 && (
            <div className={styles.noResults}>
              <p>No kits found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className={styles.infoSection}>
        <div className={styles.container}>
          <h2 className={styles.infoTitle}>What's Included in Every Kit</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <h3>Premium Canvas</h3>
              <p>High-quality pre-printed canvas with numbered sections</p>
            </div>
            <div className={styles.infoCard}>
              <h3>Acrylic Paints</h3>
              <p>
                Complete set of numbered acrylic paints in all required colors
              </p>
            </div>
            <div className={styles.infoCard}>
              <h3>Brush Set</h3>
              <p>Professional brushes in multiple sizes for detailed work</p>
            </div>
            <div className={styles.infoCard}>
              <h3>Instructions</h3>
              <p>Easy-to-follow guide with tips and techniques</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
